import type { PerspectiveCamera, Scene } from "three";
import type { GLTF } from "three/examples/jsm/Addons.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import Logger from "@/lib/logger.ts";
import type { Nullable, Nullish } from "@/types";
import { prettyNumber } from "@/utils/debug.ts";
import { isPerspectiveCamera } from "../utils/types.ts";
import type Sizes from "./utils/Sizes";

export default class Camera {
    readonly #ORBIT_CONTROL_OFFSET_TOP = 0.05;
    readonly #ZOOM_FACTOR = 0.2;

    #minDistance = 0.15;
    #maxDistance = 1;

    readonly #canvas: HTMLCanvasElement;
    readonly #scene: Scene;
    readonly #onControlsChange: (() => void) | undefined;

    #camera: Nullable<PerspectiveCamera> = null;
    #controls: Nullish<OrbitControls> = null;

    constructor(canvas: HTMLCanvasElement, scene: Scene, onControlsChange?: () => void) {
        this.#canvas = canvas;
        this.#scene = scene;
        this.#onControlsChange = onControlsChange;
    }

    get instance() {
        return this.#camera;
    }

    createCamera(model: GLTF, onLoadComplete: () => void) {
        model.parser
            .getDependencies("camera")
            .then((cameras: Camera[]) => {
                const camera = cameras[0] as Nullish<Camera>;
                if (!isPerspectiveCamera(camera)) {
                    alert("Invalid camera! Expected PerspectiveCamera");
                    return;
                }

                Logger.groupCollapsed("Camera");
                if (Logger.enabled) console.log(camera);

                this.#minDistance = this.#getZoomBoundary(camera.userData["Minimum zoom"] as Nullish<string>, this.#minDistance); // prettier-ignore
                this.#maxDistance = this.#getZoomBoundary(camera.userData["Maximum zoom"] as Nullish<string>, this.#maxDistance); // prettier-ignore

                Logger.info("Min zoom:", prettyNumber(this.#minDistance));
                Logger.info("Max zoom:", prettyNumber(this.#maxDistance));

                DiagnosticsStore.collectEventLog("info", `[Camera] Setting min zoom: ${this.#minDistance}`);
                DiagnosticsStore.collectEventLog("info", `[Camera] Setting max zoom: ${this.#maxDistance}`);

                this.#camera = camera;

                this.#scene.add(camera);
                this.#initControls(camera);

                Logger.groupEnd();
            })
            .catch((err) => {
                alert(err);
            })
            .finally(() => {
                onLoadComplete();
            });
    }

    resize(sizes: Sizes) {
        if (!this.#camera) return;

        this.#camera.aspect = sizes.aspectRatio;
        this.#camera.updateProjectionMatrix();
    }

    update() {
        this.#controls?.update();
    }

    zoomIn() {
        this.#zoom(this.#ZOOM_FACTOR);
    }

    zoomOut() {
        this.#zoom(this.#ZOOM_FACTOR * -1);
    }

    destroy() {
        if (this.#onControlsChange) {
            this.#controls?.removeEventListener("change", this.#onControlsChange);
        }
        this.#controls?.dispose();
        this.#controls = null;
    }

    #zoom(delta: number) {
        if (!this.#controls || !this.#camera) return;

        const target = this.#controls.target;
        const direction = this.#camera.position.clone().sub(target);
        const currentDistance = direction.length();

        const zoomFactor = 1 - delta;
        const newDistance = Math.min(currentDistance * zoomFactor, this.#controls.maxDistance);

        direction.normalize().multiplyScalar(newDistance);
        this.#camera.position.copy(target).add(direction);

        this.#controls.update();
    }

    #getZoomBoundary(value: Nullish<string>, fallback: number) {
        const parsed = value ? parseFloat(value) : Number.NaN;

        return !Number.isNaN(parsed) ? parsed : fallback;
    }

    #initControls(camera: PerspectiveCamera) {
        this.#controls = new OrbitControls(camera, this.#canvas);
        this.#controls.target.set(0, -0.05, 0);
        this.#controls.enableDamping = true;
        this.#controls.enablePan = false;

        this.#controls.maxPolarAngle = Math.PI; // Straight up from below (180 degrees from top)
        this.#controls.minPolarAngle = Math.PI / 2 + this.#ORBIT_CONTROL_OFFSET_TOP; // Slightly below horizontal

        this.#controls.minDistance = this.#minDistance;
        this.#controls.maxDistance = this.#maxDistance;

        if (this.#onControlsChange) {
            this.#controls.addEventListener("change", this.#onControlsChange);
        }
    }
}
